import {
	Button,
	Icon,
	SettingsGroup,
	SettingsItemCheckbox,
	SettingsItemTextField,
	TextField,
} from '@elipssolution/harfang';
import { mdiPencil } from '@mdi/js';
import { ListItemText, Stack, styled } from '@mui/material';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import sanitizeHtml from 'sanitize-html';

import { calculateVisibleLines } from './utils/calculateVisibleLines';
import { sanitizeOptions } from './utils/sanitizeOptions';
import SettingsDialogPage from '../../../components/SettingsDialogPage';
import useFormat from '../../../hooks/useFormat';
import { WidgetComponentProps, WidgetConfigurationComponentProps } from '../../../types/widget';

const MAX_VIEW_HEIGHT = 25;

type WidgetHtmlConfigurationType = {
	html?: string;
};

const WidgetHtmlWrapper = styled('div')({
	display: 'flex',
	flex: 1,

	'&.readOnly': {
		pointerEvents: 'none',
	},
});

const BaseSettingsItem = styled('div')(({ theme: { palette, shape, spacing, shadows } }) => ({
	flex: 1,
	display: 'flex',
	alignItems: 'flex-start',
	flexDirection: 'column',
	padding: spacing(0.5, 1.5, 1.5, 1.5),
	boxShadow: shadows[1],
	borderRadius: shape.borderRadius,
	border: `1px solid ${palette.divider}`,

	'& > div:first-of-type': {
		flex: 0,
	},

	'& > div:last-of-type': {
		flex: 1,

		'& > div': {
			flex: 1,

			'& > textarea': {
				height: '100% !important',
				overflow: 'scroll !important',
			},
		},
	},
}));

const ContentWrapper = styled('div', {
	shouldForwardProp: (prop: string) => prop !== 'lineClamp',
})(({ lineClamp }: { lineClamp?: number }) => ({
	border: 0,

	'&.isMobileFormat': {
		// LineClamp does not work properly with older versions of mobile browsers that do not support "dvh"
		// Therefore, LineClamp is only used for recent mobile versions that support "dvh"
		'@supports(height: 100dvh)': {
			display: ' -webkit-box',
			WebkitLineClamp: lineClamp,
			WebkitBoxOrient: 'vertical',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxHeight: `${MAX_VIEW_HEIGHT}dvh`,
		},

		maxHeight: `${MAX_VIEW_HEIGHT}vh`,
	},
}));

export const WidgetHtmlConfiguration = ({
	name: initialName,
	isAvailableOnDesktopFormat: initialIsAvailableOnDesktopFormat,
	isAvailableOnMobileFormat: initialIsAvailableOnMobileFormat,
	configuration: initialConfiguration,
	onBack,
	onSave,
}: WidgetConfigurationComponentProps) => {
	const [name, setName] = useState(initialName ?? 'HTML');
	const [isAvailableOnDesktopFormat, setIsAvailableOnDesktopFormat] = useState(initialIsAvailableOnDesktopFormat);
	const [isAvailableOnMobileFormat, setIsAvailableOnMobileFormat] = useState(initialIsAvailableOnMobileFormat);
	const [configuration, setConfiguration] = useState(initialConfiguration as WidgetHtmlConfigurationType | undefined);

	return (
		<SettingsDialogPage title={name} onBack={onBack}>
			<SettingsItemTextField
				label="Nom du widget"
				value={name}
				onChange={(changedName) => setName(changedName ?? '')}
			/>

			<SettingsGroup label="Configuration">
				<SettingsItemCheckbox
					checked={isAvailableOnDesktopFormat}
					description="Si coché, le widget sera visible sur ordinateur."
					label="Visible version ordinateur"
					onChange={(checked: boolean) => setIsAvailableOnDesktopFormat(checked)}
				/>
				<SettingsItemCheckbox
					checked={isAvailableOnMobileFormat}
					description="Si coché, le widget sera visible sur mobile."
					label="Visible version mobile"
					onChange={(checked: boolean) => setIsAvailableOnMobileFormat(checked)}
				/>
			</SettingsGroup>

			<BaseSettingsItem>
				<ListItemText primary="Contenu" />
				<TextField onChange={(value) => setConfiguration({ html: value })} value={configuration?.html} multiline />
			</BaseSettingsItem>

			<Stack direction="row" justifyContent="flex-end">
				<Button
					startIcon={<Icon path={mdiPencil} size="small" />}
					variant="contained"
					onClick={() => onSave(name, isAvailableOnDesktopFormat, isAvailableOnMobileFormat, configuration)}
				>
					Appliquer
				</Button>
			</Stack>
		</SettingsDialogPage>
	);
};

const WidgetHtml = ({ configuration, readOnly }: WidgetComponentProps) => {
	const { isMobileFormat } = useFormat();

	const contentRef = useRef<HTMLDivElement | null>(null);

	const [lineClamp, setLineClamp] = useState(0);

	const { html = '<p>Hello World!</p>' } = (configuration as WidgetHtmlConfigurationType | undefined) ?? {};

	useEffect(() => {
		if (!contentRef.current || !isMobileFormat) return;

		const visibleLinesNb = calculateVisibleLines(contentRef.current, MAX_VIEW_HEIGHT);

		setLineClamp(visibleLinesNb);
	}, [html, isMobileFormat]);

	return (
		<WidgetHtmlWrapper className={clsx({ readOnly })}>
			<ContentWrapper
				lineClamp={lineClamp}
				ref={contentRef}
				className={clsx({ isMobileFormat })}
				dangerouslySetInnerHTML={{
					__html: sanitizeHtml(html, sanitizeOptions),
				}}
			/>
		</WidgetHtmlWrapper>
	);
};

export default WidgetHtml;
